/**
 * Prufe Offline Sync Engine
 *
 * Manages bidirectional sync between the JMAP server and local IndexedDB cache.
 * Enables offline-first operation for desktop and mobile apps.
 *
 * Strategy:
 * - On app load: call Email/changes to get delta since last sync state
 * - On reconnect: reconcile local changes with server state
 * - Outbox: emails composed offline are queued and sent on reconnect
 * - Conflict resolution: server wins (last-write-wins for flags/moves)
 *
 * IndexedDB schema:
 * - emails: { emailId, accountId, ...email data }
 * - mailboxes: { mailboxId, accountId, ...mailbox data }
 * - threads: { threadId, accountId, emailIds[] }
 * - contacts: { contactId, accountId, ...contact data }
 * - calendarEvents: { eventId, accountId, ...event data }
 * - outbox: { id, type, payload, createdAt, status }
 * - syncState: { accountId, entityType, state }
 */

const DB_NAME = 'prufe-cache';
const DB_VERSION = 1;

export interface SyncState {
  accountId: string;
  entityType: string;
  state: string;
  lastSyncAt: string;
}

export interface OutboxItem {
  id: string;
  type: 'send' | 'update' | 'delete';
  method: string;
  payload: unknown;
  createdAt: string;
  status: 'pending' | 'sending' | 'failed';
  retries: number;
}

class SyncEngine {
  private db: IDBDatabase | null = null;
  private isOnline: boolean = navigator.onLine;
  private syncInterval: ReturnType<typeof setInterval> | null = null;

  async init(): Promise<void> {
    this.db = await this.openDatabase();

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processOutbox();
      this.syncAll();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Periodic sync every 60 seconds when online
    this.syncInterval = setInterval(() => {
      if (this.isOnline) {
        this.syncAll();
      }
    }, 60000);
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('emails')) {
          const emailStore = db.createObjectStore('emails', { keyPath: 'emailId' });
          emailStore.createIndex('accountId', 'accountId', { unique: false });
          emailStore.createIndex('mailboxIds', 'mailboxIds', { unique: false, multiEntry: true });
          emailStore.createIndex('threadId', 'threadId', { unique: false });
        }

        if (!db.objectStoreNames.contains('mailboxes')) {
          const mboxStore = db.createObjectStore('mailboxes', { keyPath: 'mailboxId' });
          mboxStore.createIndex('accountId', 'accountId', { unique: false });
        }

        if (!db.objectStoreNames.contains('contacts')) {
          const contactStore = db.createObjectStore('contacts', { keyPath: 'contactId' });
          contactStore.createIndex('accountId', 'accountId', { unique: false });
        }

        if (!db.objectStoreNames.contains('calendarEvents')) {
          const eventStore = db.createObjectStore('calendarEvents', { keyPath: 'eventId' });
          eventStore.createIndex('accountId', 'accountId', { unique: false });
        }

        if (!db.objectStoreNames.contains('outbox')) {
          db.createObjectStore('outbox', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('syncState')) {
          db.createObjectStore('syncState', { keyPath: ['accountId', 'entityType'] });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /** Get cached data from IndexedDB */
  async getCached<T>(storeName: string, accountId: string): Promise<T[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index('accountId');
      const request = index.getAll(accountId);

      request.onsuccess = () => resolve(request.result as T[]);
      request.onerror = () => reject(request.error);
    });
  }

  /** Store data in IndexedDB */
  async putCached(storeName: string, items: unknown[]): Promise<void> {
    if (!this.db || items.length === 0) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      for (const item of items) {
        store.put(item);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Delete items from IndexedDB */
  async deleteCached(storeName: string, keys: string[]): Promise<void> {
    if (!this.db || keys.length === 0) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      for (const key of keys) {
        store.delete(key);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Add an item to the outbox for offline sending */
  async addToOutbox(item: Omit<OutboxItem, 'id' | 'createdAt' | 'status' | 'retries'>): Promise<void> {
    const outboxItem: OutboxItem = {
      ...item,
      id: `outbox_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      createdAt: new Date().toISOString(),
      status: 'pending',
      retries: 0,
    };

    await this.putCached('outbox', [outboxItem]);
  }

  /** Process pending outbox items (called when coming back online) */
  async processOutbox(): Promise<void> {
    if (!this.db || !this.isOnline) return;

    const { jmapClient } = await import('../api/jmapClient');
    if (!jmapClient.isConfigured()) return;

    const tx = this.db.transaction('outbox', 'readonly');
    const store = tx.objectStore('outbox');
    const request = store.getAll();

    request.onsuccess = async () => {
      const items = request.result as OutboxItem[];
      const pending = items.filter((i) => i.status === 'pending' || i.status === 'failed');

      for (const item of pending) {
        try {
          const payload = item.payload as Record<string, unknown>;
          await jmapClient.invoke(item.method, payload);

          // On success, remove from outbox
          await this.deleteCached('outbox', [item.id]);
        } catch (e) {
          console.error(`Outbox item failed: ${item.id}`, e);
          item.retries += 1;
          item.status = item.retries >= 3 ? 'failed' : 'pending';
          await this.putCached('outbox', [item]);
        }
      }
    };
  }

  /** Sync all entity types with the server */
  async syncAll(): Promise<void> {
    if (!this.isOnline) return;

    const { jmapClient } = await import('../api/jmapClient');
    if (!jmapClient.isConfigured()) return;

    try {
      await this.syncEntity('Email', 'emails', 'emailId', jmapClient);
      await this.syncEntity('Mailbox', 'mailboxes', 'mailboxId', jmapClient);
    } catch (e) {
      console.warn('Sync failed:', e);
    }
  }

  /** Sync a single entity type using JMAP /changes */
  private async syncEntity(
    entityType: string,
    storeName: string,
    keyField: string,
    client: { invoke: (method: string, args: Record<string, unknown>) => Promise<Record<string, unknown>> },
  ): Promise<void> {
    if (!this.db) return;

    // 1. Get last sync state from IndexedDB
    const syncState = await this.getSyncState(entityType);
    const sinceState = syncState?.state || '0';

    // 2. Call {Entity}/changes with sinceState
    const changes = await client.invoke(`${entityType}/changes`, { sinceState });

    const created = (changes.created as string[]) || [];
    const updated = (changes.updated as string[]) || [];
    const destroyed = (changes.destroyed as string[]) || [];

    // 3. Fetch created/updated items
    const idsToFetch = [...created, ...updated];
    if (idsToFetch.length > 0) {
      const result = await client.invoke(`${entityType}/get`, { ids: idsToFetch });
      const list = (result.list as unknown[]) || [];
      if (list.length > 0) {
        await this.putCached(storeName, list);
      }
    }

    // 4. Delete destroyed items from cache
    if (destroyed.length > 0) {
      await this.deleteCached(storeName, destroyed);
    }

    // 5. Update sync state
    const newState = (changes.newState as string) || '1';
    await this.putSyncState(entityType, newState);
  }

  /** Get sync state for an entity type */
  private async getSyncState(entityType: string): Promise<SyncState | null> {
    if (!this.db) return null;

    return new Promise((resolve) => {
      const tx = this.db!.transaction('syncState', 'readonly');
      const store = tx.objectStore('syncState');
      const request = store.get([entityType, entityType]);
      request.onsuccess = () => resolve(request.result as SyncState | null);
      request.onerror = () => resolve(null);
    });
  }

  /** Update sync state for an entity type */
  private async putSyncState(entityType: string, state: string): Promise<void> {
    if (!this.db) return;

    const syncState: SyncState = {
      accountId: entityType, // keyed by entity type for simplicity
      entityType,
      state,
      lastSyncAt: new Date().toISOString(),
    };

    await this.putCached('syncState', [syncState]);
  }

  /** Get the current sync status */
  getStatus(): { online: boolean; outboxCount: number } {
    return {
      online: this.isOnline,
      outboxCount: this._outboxCount,
    };
  }

  private _outboxCount = 0;

  /** Update outbox count */
  async refreshOutboxCount(): Promise<number> {
    if (!this.db) return 0;

    return new Promise((resolve) => {
      const tx = this.db!.transaction('outbox', 'readonly');
      const store = tx.objectStore('outbox');
      const request = store.count();
      request.onsuccess = () => {
        this._outboxCount = request.result;
        resolve(request.result);
      };
      request.onerror = () => resolve(0);
    });
  }

  /** Clean up on app shutdown */
  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    if (this.db) {
      this.db.close();
    }
  }
}

// Singleton instance
export const syncEngine = new SyncEngine();
